import { BASE_API } from '../../utils/constants';

const jsonHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

const normalizeList = (data) => Array.isArray(data) ? data : (data?.results || []);

async function request(path, token, options = {}) {
  const response = await fetch(`${BASE_API}${path}`, {
    ...options,
    headers: { ...jsonHeaders(token), ...(options.headers || {}) },
  });
  const data = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.detail || 'No se pudo completar la operación.');
    error.data = data;
    throw error;
  }
  return data;
}

export const getClients = async (token, search = '') => normalizeList(await request(
  `/api/finance/clients/?page_size=${search.length >= 3 ? 200 : 20}${search.length >= 3 ? `&search=${encodeURIComponent(search)}` : ''}`,
  token,
));

export const getServices = async (token, search = '') => normalizeList(await request(
  `/api/finance/services/?active=true&page_size=${search.length >= 3 ? 200 : 20}&ordering=name${search.length >= 3 ? `&search=${encodeURIComponent(search)}` : ''}`, token,
));

export const getAllServices = async (token) => normalizeList(await request(
  '/api/finance/services/?page_size=200&ordering=name', token,
));

export const getAcquisitionChannels = async (token) => normalizeList(await request(
  '/api/crm/sources/?page_size=200&ordering=name', token,
));

export const previewSale = (token, payload) => request('/api/finance/sales/preview/', token, {
  method: 'POST', body: JSON.stringify(payload),
});

export const createSale = (token, payload) => request('/api/finance/sales/', token, {
  method: 'POST', body: JSON.stringify(payload),
});

export const getSalesByClient = async (token, clientId) => normalizeList(await request(
  `/api/finance/sales/?client=${clientId}&page_size=200&ordering=-issue_date`, token,
));

export const getClientSales = (token, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/api/finance/sales/client-list/${query ? `?${query}` : ''}`, token);
};

export const getSale = (token, id) => request(`/api/finance/sales/${id}/`, token);

export const getLegacySales = (token, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/api/finance/sales/legacy-regularization/${query ? `?${query}` : ''}`, token);
};

export const getLegacySaleReview = (token, id) => request(
  `/api/finance/sales/${id}/legacy-regularization/`, token,
);

export const previewLegacySaleReview = (token, id, payload) => request(
  `/api/finance/sales/${id}/legacy-regularization/preview/`, token,
  { method: 'POST', body: JSON.stringify(payload) },
);

export const applyLegacySaleReview = (token, id, payload) => request(
  `/api/finance/sales/${id}/legacy-regularization/apply/`, token,
  { method: 'POST', body: JSON.stringify(payload) },
);

export const deleteLegacySalePayment = (token, saleId, paymentId) => request(
  `/api/finance/sales/${saleId}/legacy-regularization/payments/${paymentId}/`, token,
  { method: 'DELETE' },
);

export const updateSale = (token, id, payload) => request(`/api/finance/sales/${id}/`, token, {
  method: 'PATCH', body: JSON.stringify(payload),
});

export const assignSalePayments = (token, id, assignments) => request(
  `/api/finance/sales/${id}/assign-installments/`, token,
  { method: 'POST', body: JSON.stringify({ assignments }) },
);

export const createPayment = (token, payload) => request('/api/finance/payments/', token, {
  method: 'POST', body: JSON.stringify(payload),
});

export const updatePayment = (token, id, payload) => request(`/api/finance/payments/${id}/`, token, {
  method: 'PATCH', body: JSON.stringify(payload),
});

export const regularizePaymentDetraction = (token, id, payload) => request(
  `/api/finance/payments/${id}/regularize-detraction/`, token,
  { method: 'POST', body: JSON.stringify(payload) },
);

export const getExpenseCategories = async (token) => normalizeList(await request(
  '/api/finance/expense-categories/?page_size=200&ordering=name', token,
));

export const createExpense = (token, payload) => request('/api/finance/expenses/', token, {
  method: 'POST', body: JSON.stringify(payload),
});

export const getExpenses = async (token, dateAfter, dateBefore) => normalizeList(await request(
  `/api/finance/expenses/?date_after=${dateAfter}&date_before=${dateBefore}&ordering=date&page_size=200`, token,
));

export const getRecurringExpenseStatus = (token, date) => request(
  `/api/recurring/recurring-expenses/status/?date=${encodeURIComponent(date)}`, token,
);

export const payRecurringExpense = (token, id, payload) => request(
  `/api/recurring/recurring-expenses/${id}/pay/`, token,
  { method: 'POST', body: JSON.stringify(payload) },
);

export const getRecurringSaleStatus = (token, date) => request(
  `/api/recurring/recurring-sales/status/?date=${encodeURIComponent(date)}`, token,
);

export const registerRecurringSale = (token, id, payload) => request(
  `/api/recurring/recurring-sales/${id}/register/`, token,
  { method: 'POST', body: JSON.stringify(payload) },
);

export const getGeneralSummary = (token, params) => {
  const query = new URLSearchParams(params).toString();
  return request(`/api/finance/general-summary/?${query}`, token);
};

export const getHomeSummary = (token, month) => request(
  `/api/finance/home-summary/?month=${encodeURIComponent(month)}`, token,
);

export const getGeneralSummaryMovements = (token, params) => {
  const query = new URLSearchParams(params).toString();
  return request(`/api/finance/general-summary/movements/?${query}`, token);
};

export const getGeneralSummaryReceivables = (token, params) => {
  const query = new URLSearchParams(params).toString();
  return request(`/api/finance/general-summary/receivables/?${query}`, token);
};

export const getLeads = (token, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/api/crm/leads/${query ? `?${query}` : ''}`, token);
};

export const getLeadStages = async (token) => normalizeList(await request(
  '/api/crm/stages/?ordering=order', token,
));

export const getLeadSources = async (token) => normalizeList(await request(
  '/api/crm/sources/?ordering=name', token,
));

export const createLead = (token, payload) => request('/api/crm/leads/', token, {
  method: 'POST', body: JSON.stringify(payload),
});

export const updateLead = (token, id, payload) => request(`/api/crm/leads/${id}/`, token, {
  method: 'PATCH', body: JSON.stringify(payload),
});

export const archiveLead = (token, id) => request(`/api/crm/leads/${id}/archive/`, token, {
  method: 'POST', body: '{}',
});

export const setLeadPriority = (token, id, isPriority) => request(`/api/crm/leads/${id}/priority/`, token, {
  method: 'PATCH', body: JSON.stringify({ is_priority: isPriority }),
});

export const recordLeadContact = (token, id, payload) => request(`/api/crm/leads/${id}/contact/`, token, {
  method: 'POST', body: JSON.stringify(payload),
});

export const getLeadActivities = (token, id) => request(`/api/crm/leads/${id}/activities/`, token);

export const addLeadNote = (token, id, description) => request(`/api/crm/leads/${id}/activities/`, token, {
  method: 'POST', body: JSON.stringify({ description }),
});

export const getLeadSales = (token, id) => request(`/api/crm/leads/${id}/sales/`, token);

export const getLeadSaleCandidates = (token, id, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/api/crm/leads/${id}/sale-candidates/${query ? `?${query}` : ''}`, token);
};

export const linkLeadSale = (token, id, saleId) => request(`/api/crm/leads/${id}/link-sale/`, token, {
  method: 'POST', body: JSON.stringify({ sale_id: saleId }),
});

export const unlinkLeadSale = (token, id, saleId) => request(`/api/crm/leads/${id}/unlink-sale/`, token, {
  method: 'POST', body: JSON.stringify({ sale_id: saleId }),
});

export const getLeadDashboard = (token, month) => request(
  `/api/crm/dashboard/?month=${encodeURIComponent(month)}`, token,
);
